import * as fabric from 'fabric'
import {
  EditorColorType,
  EditorColoreApiType,
  EditorObjectType,
  NumeroColoriType,
} from '@utils/types'
import {
  applyAlphaToHexColor,
  convertHexToCmyk,
  convertHexToPantone,
  normalizeHexColor,
  splitHexColorAndAlpha,
} from './index'
import { getPantoneHex } from './colors'
import {
  applyHiddenSerializedMonochrome,
  updateHiddenSerializedColors,
} from './fabric-editor-hidden-colors'

/**
 * Logiche colore specifiche del solo `useFabricEditor`.
 * Qui teniamo conversioni/mapping/applicazione colori e gli handler runtime Fabric.
 */

/**
 * Deriva il tipo di input colore da mostrare in editor partendo dalla configurazione attiva.
 * Le tecniche full color espongono CMYK, tutte le altre usano Pantone.
 */
export const getPreferredColorVariant = (
  configurazioneNumeroColori?: NumeroColoriType | { full_color?: boolean } | null
): EditorColorType['variant'] =>
  configurazioneNumeroColori?.full_color === true ? 'cmyk' : 'pantone'

/**
 * Restituisce il valore leggibile da mostrare in UI a partire da un HEX normalizzato.
 * Per CMYK torna una stringa `(c,m,y,k)`, per Pantone l'oggetto del pantone più vicino.
 */
export const getColorDisplayValue = (
  hex: string,
  variant: EditorColorType['variant'],
  pantoni: EditorColoreApiType[] = []
) => (variant === 'cmyk' ? convertHexToCmyk(hex) : convertHexToPantone(hex, pantoni))

/**
 * Adatta un colore al variant di destinazione.
 * Quando il target è Pantone, converte l'HEX corrente nel Pantone più vicino.
 * Se viene passata una palette ammessa, il matching avviene sempre dentro quella palette,
 * anche quando il colore era già marcato come `pantone`.
 */
export const getNormalizedValueForVariant = (
  hex: string,
  currentVariant: EditorColorType['variant'] | undefined,
  targetVariant: EditorColorType['variant'],
  pantoni: EditorColoreApiType[] = []
) => {
  const normalizedHex = normalizeHexColor(hex)

  if (targetVariant !== 'pantone') {
    return normalizedHex
  }

  const pantone = convertHexToPantone(normalizedHex, pantoni)
  if (currentVariant === 'pantone' && pantoni.length === 0) {
    return normalizedHex
  }

  return normalizeHexColor(pantone?.hex || normalizedHex)
}

/**
 * Riallinea un singolo `EditorColorType` al variant richiesto aggiornando:
 * `id`, `value`, `originalValue`, `variant` e `displayValue`.
 */
export const normalizeEditorColorVariant = (
  color: EditorColorType | undefined,
  targetVariant: EditorColorType['variant'],
  pantoni: EditorColoreApiType[] = []
) => {
  if (!color) return color

  const normalizedValue = getNormalizedValueForVariant(
    color.value || color.originalValue || color.id,
    color.variant,
    targetVariant,
    pantoni
  )
  const normalizedOriginalValue = normalizeHexColor(
    color.originalValue || color.id || normalizedValue
  )

  return {
    ...color,
    id: normalizeHexColor(color.id || normalizedOriginalValue),
    value: normalizedValue,
    originalValue: normalizedOriginalValue,
    variant: targetVariant,
    displayValue: getColorDisplayValue(normalizedValue, targetVariant, pantoni),
  }
}

/**
 * Aggiorna solo il valore corrente di un colore preservandone il variant
 * e rigenerando il relativo `displayValue`.
 */
export const updateEditorColorValue = (
  color: EditorColorType | undefined,
  nextValue: string,
  pantoni: EditorColoreApiType[] = []
) => {
  if (!color) return color

  const normalizedValue = normalizeHexColor(nextValue)
  return {
    ...color,
    value: normalizedValue,
    displayValue: getColorDisplayValue(normalizedValue, color.variant, pantoni),
  }
}

/**
 * Applica il variant richiesto a un intero object control dell'editor.
 * Gestisce sia immagini (`colori`, `removedBackgroundColor`) sia testi (`colore`).
 */
export const normalizeObjectControlColorVariant = (
  control: EditorObjectType,
  targetVariant: EditorColorType['variant'],
  pantoni: EditorColoreApiType[] = []
) => {
  if (!control) return control

  if (control.etype === 'image') {
    return {
      ...control,
      colori: (control.colori || []).map((color) =>
        normalizeEditorColorVariant(color, targetVariant, pantoni)
      ),
      removedBackgroundColor: normalizeEditorColorVariant(
        control.removedBackgroundColor,
        targetVariant,
        pantoni
      ),
    }
  }

  if (control.etype === 'text') {
    return {
      ...control,
      colore: normalizeEditorColorVariant(control.colore, targetVariant, pantoni),
    }
  }

  return control
}

/**
 * Verifica se un object control contiene ancora colori espressi con un variant diverso
 * da quello richiesto dalla configurazione corrente.
 */
export const hasObjectControlVariantMismatch = (
  control: EditorObjectType,
  targetVariant: EditorColorType['variant']
) => {
  if (!control) return false

  if (control.etype === 'image') {
    return (
      (control.colori || []).some((color) => color?.variant !== targetVariant) ||
      (control.removedBackgroundColor?.variant !== undefined &&
        control.removedBackgroundColor.variant !== targetVariant)
    )
  }

  if (control.etype === 'text') {
    return control.colore?.variant !== undefined && control.colore.variant !== targetVariant
  }

  return false
}

/**
 * Normalizza tutti i fill/stroke di un vettoriale all'HEX del Pantone più vicino.
 */
export const normalizeVectorColorsToPantone = (
  rootObject: any,
  forcedSingleColorHex?: string,
  pantoni: EditorColoreApiType[] = []
) => {
  if (!rootObject) return

  const traverse = (obj: any) => {
    if (!obj) return

    if (obj.type === 'group' && obj._objects) {
      obj._objects.forEach(traverse)
      return
    }

    if (obj.fill && typeof obj.fill === 'string' && obj.fill !== 'transparent') {
      try {
        const sourceFill = normalizeHexColor(obj.fill)
        const pantoneSourceFill = getPantoneHex(sourceFill, pantoni)
        const normalizedFill = forcedSingleColorHex || sourceFill

        // Manteniamo il riferimento colore "di slot" prima di eventuali forzature
        // monocromatiche. In questo modo, se la tecnica cambia, possiamo tornare
        // al colore Pantone corretto di ogni tracciato invece di restare su un
        // singolo colore uniforme.
        if (obj.originalFill === undefined && pantoneSourceFill) {
          obj.originalFill = pantoneSourceFill
        }

        obj.set('fill', getPantoneHex(normalizedFill, pantoni))
      } catch (error) {
        console.warn(`Could not normalize fill color: ${obj.fill}`, error)
      }
    }

    if (obj.stroke && typeof obj.stroke === 'string' && obj.stroke !== 'transparent') {
      try {
        const sourceStroke = normalizeHexColor(obj.stroke)
        const pantoneSourceStroke = getPantoneHex(sourceStroke, pantoni)
        const normalizedStroke = forcedSingleColorHex || sourceStroke

        if (obj.originalStroke === undefined && pantoneSourceStroke) {
          obj.originalStroke = pantoneSourceStroke
        }

        obj.set('stroke', getPantoneHex(normalizedStroke, pantoni))
      } catch (error) {
        console.warn(`Could not normalize stroke color: ${obj.stroke}`, error)
      }
    }
  }

  traverse(rootObject)
}

/**
 * Forza tutti i fill/stroke di un vettoriale a un singolo HEX senza conversioni Pantone.
 */
export const normalizeVectorColorsToSingleColor = (
  rootObject: any,
  forcedSingleColorHex?: string
) => {
  if (!rootObject || !forcedSingleColorHex) return

  const normalizedForcedColor = normalizeHexColor(forcedSingleColorHex)
  if (!normalizedForcedColor) return

  const traverse = (obj: any) => {
    if (!obj) return

    if (obj.type === 'group' && obj._objects) {
      obj._objects.forEach(traverse)
      return
    }

    if (obj.fill && typeof obj.fill === 'string' && obj.fill !== 'transparent') {
      try {
        if (obj.originalFill === undefined) {
          obj.originalFill = normalizeHexColor(obj.fill)
        }
        obj.set('fill', normalizedForcedColor, forcedSingleColorHex)
      } catch (error) {
        console.warn(`Could not force fill color: ${obj.fill}`, error)
      }
    }

    if (obj.stroke && typeof obj.stroke === 'string' && obj.stroke !== 'transparent') {
      try {
        if (obj.originalStroke === undefined) {
          obj.originalStroke = normalizeHexColor(obj.stroke)
        }
        obj.set('stroke', normalizedForcedColor)
      } catch (error) {
        console.warn(`Could not force stroke color: ${obj.stroke}`, error)
      }
    }
  }

  traverse(rootObject)
}

/**
 * Estrae i colori distinti visibili da un oggetto Fabric vettoriale e li converte
 * nella struttura `EditorColorType` usata dalla sidebar.
 */
export const extractObjectColors = (
  rootObject: any,
  editorMeta: any,
  variant: EditorColorType['variant'],
  pantoni: EditorColoreApiType[] = []
) => {
  if (!rootObject) return []

  // Gli slot sidebar devono sopravvivere anche quando il canvas è già stato
  // forzato a un colore globale. Per questo deduplichiamo per colore originale
  // (`originalFill/originalStroke`) e conserviamo separatamente il valore
  // corrente visibile (`fill/stroke`).
  const colorEntries = new Map<
    string,
    {
      value: string
      originalValue: string
    }
  >()
  const isVector = !!editorMeta?.is_vector

  if (!isVector) return []

  const traverse = (obj: any) => {
    if (!obj) return

    if (obj.type === 'group' && obj._objects) {
      obj._objects.forEach(traverse)
      return
    }

    if (obj.fill && typeof obj.fill === 'string' && obj.fill !== 'transparent' && obj.visible) {
      try {
        const normalizedCurrentColor = normalizeHexColor(obj.fill)
        const normalizedOriginalColor = normalizeHexColor(obj.originalFill || obj.fill)
        if (normalizedOriginalColor) {
          colorEntries.set(normalizedOriginalColor, {
            value: normalizedCurrentColor || normalizedOriginalColor,
            originalValue: normalizedOriginalColor,
          })
        }
      } catch (e) {
        console.warn(`Could not parse fill color: ${obj.fill}`, e)
      }
    }

    if (
      obj.stroke &&
      typeof obj.stroke === 'string' &&
      obj.stroke !== 'transparent' &&
      obj.strokeWidth > 0 &&
      obj.visible
    ) {
      try {
        const normalizedCurrentColor = normalizeHexColor(obj.stroke)
        const normalizedOriginalColor = normalizeHexColor(obj.originalStroke || obj.stroke)
        if (normalizedOriginalColor) {
          colorEntries.set(normalizedOriginalColor, {
            value: normalizedCurrentColor || normalizedOriginalColor,
            originalValue: normalizedOriginalColor,
          })
        }
      } catch (e) {
        console.warn(`Could not parse stroke color: ${obj.stroke}`, e)
      }
    }
  }

  traverse(rootObject)

  return Array.from(colorEntries.values()).map(
    ({ value, originalValue }) =>
      ({
        id: normalizeHexColor(originalValue),
        value: normalizeHexColor(value),
        originalValue: normalizeHexColor(originalValue),
        variant,
        displayValue: getColorDisplayValue(normalizeHexColor(value), variant, pantoni),
      } as EditorColorType)
  )
}

/**
 * Costruisce la mappa `colore originale -> colore aggiornato` confrontando due array
 * di slot colore nella stessa posizione logica.
 */
export const buildColorMappings = (
  currentColors: EditorColorType[] = [],
  nextColors: EditorColorType[] = []
) => {
  const mappings = new Map<string, string>()

  currentColors.forEach((currentColor, index) => {
    const nextColor = nextColors[index]
    if (!currentColor?.value || !nextColor?.value || currentColor.value === nextColor.value) {
      return
    }

    mappings.set(normalizeHexColor(currentColor.value), normalizeHexColor(nextColor.value))
  })

  return mappings
}

/**
 * Applica una mappa di sostituzione colori a un oggetto Fabric, visitando ricorsivamente
 * gruppi, fill e stroke.
 */
export const applyColorMappingsToFabricObject = (
  rootObject: any,
  colorMappings: Map<string, string>
) => {
  if (!rootObject || colorMappings.size === 0) return

  const traverse = (obj: any) => {
    if (!obj) return

    if (obj.type === 'group' && obj._objects) {
      obj._objects.forEach(traverse)
      return
    }

    if (obj.fill && typeof obj.fill === 'string' && obj.fill !== 'transparent') {
      const normalizedFill = normalizeHexColor(obj.fill)
      const nextFill = colorMappings.get(normalizedFill)
      if (nextFill) obj.set('fill', nextFill)
    }

    if (obj.stroke && typeof obj.stroke === 'string' && obj.stroke !== 'transparent') {
      const normalizedStroke = normalizeHexColor(obj.stroke)
      const nextStroke = colorMappings.get(normalizedStroke)
      if (nextStroke) obj.set('stroke', nextStroke)
    }
  }

  traverse(rootObject)
}

export const freezeOriginalColors = (obj: any) => {
  if (obj._objects) {
    obj._objects.forEach(freezeOriginalColors)
  } else {
    if (obj.fill && obj.originalFill === undefined) {
      obj.originalFill = obj.fill
    }
    if (obj.stroke && obj.originalStroke === undefined) {
      obj.originalStroke = obj.stroke
    }
  }
}

export const resetObjectToOriginalColor = ({
  canvas,
  id,
}: {
  canvas: fabric.Canvas | null | undefined
  id: string | number
}) => {
  const fabricObject = canvas?.getObjects().find((o: any) => String(o?.customId) === String(id))
  if (!fabricObject) return

  const restore = (o: any) => {
    if (o._objects) {
      o._objects.forEach(restore)
    } else {
      if (o.originalFill !== undefined) o.set('fill', o.originalFill)
      if (o.originalStroke !== undefined) o.set('stroke', o.originalStroke)
    }
  }

  restore(fabricObject)
  applyHiddenSerializedMonochrome(fabricObject, null)
}

export const findBackgroundObject = (logoObject: any) => {
  let backgroundElement = null

  if (logoObject.type === 'group' && logoObject._objects) {
    const objects = logoObject.getObjects()

    const logoBox = logoObject.getBoundingRect()
    const logoArea = logoBox.width * logoBox.height

    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i]
      const overallBBox = obj.getBoundingRect()
      const overallArea = overallBBox.width * overallBBox.height
      const ratio = overallArea / logoArea

      if (ratio >= 0.95) {
        backgroundElement = obj
        break
      }
    }

    if (backgroundElement) {
      backgroundElement.isBackground = true
      logoObject.hasBackground = true
    }
  }

  return backgroundElement
}

export const handleColorChange = ({
  canvas,
  id,
  originalColorHex,
  newColorHex,
  objectControls,
  isGlobalPantoniColorMode,
  specialCanvasObjectIds,
  pantoneReference = [],
  syncOriginalValue = false,
  setObjectControls,
  onExportOverlayOnly,
}: {
  canvas: fabric.Canvas | null | undefined
  id: string | number
  originalColorHex: string
  newColorHex: string
  objectControls: EditorObjectType[]
  isGlobalPantoniColorMode: boolean
  specialCanvasObjectIds: Array<string | number>
  pantoneReference?: EditorColoreApiType[]
  syncOriginalValue?: boolean
  setObjectControls: (updater: (prev: EditorObjectType[]) => EditorObjectType[]) => void
  onExportOverlayOnly: () => void
}) => {
  if (!canvas) return

  const normalizedOriginalColorHex = normalizeHexColor(originalColorHex)
  const normalizedNewColorHex = normalizeHexColor(newColorHex)
  if (!normalizedNewColorHex) return

  const control = objectControls.find((oc) => oc.id === id)
  if (!control && !isGlobalPantoniColorMode) return

  const resolveSyncedOriginalColor = (sourceHex?: string) => {
    const normalizedSource = normalizeHexColor(sourceHex || '')
    if (!syncOriginalValue) return normalizedSource
    if (!normalizedSource) return normalizedNewColorHex
    return getPantoneHex(normalizedSource, pantoneReference)
  }

  const updateEditorColorOriginalValue = (color: EditorColorType | undefined) => {
    const nextColor = updateEditorColorValue(color, normalizedNewColorHex, pantoneReference)
    if (!nextColor || !syncOriginalValue) return nextColor

    return {
      ...nextColor,
      originalValue: resolveSyncedOriginalColor(
        color?.originalValue || color?.id || color?.value || normalizedNewColorHex
      ),
    }
  }

  // Nei cambi manuali la sidebar passa sempre l'identità originale dello slot
  // (`originalValue`/`id`). Se due slot diversi condividono temporaneamente lo
  // stesso colore corrente, fare match anche sul valore visibile li accoppierebbe.
  // Il fallback sul colore corrente serve solo nei riallineamenti automatici di
  // palette/tecnica, dove `syncOriginalValue` è esplicitamente attivo.
  const shouldMatchCurrentColor = syncOriginalValue

  const applyColorToFabricObject = (
    obj: any,
    forceAll: boolean,
    isMonochrome: boolean,
    isText: boolean
  ) => {
    if (!obj) return
    if (obj._objects) {
      obj._objects.forEach((child: any) =>
        applyColorToFabricObject(child, forceAll, isMonochrome, isText)
      )
      return
    }

    const normalizedOriginalFill =
      typeof obj.originalFill === 'string' ? normalizeHexColor(obj.originalFill) : null
    const normalizedOriginalStroke =
      typeof obj.originalStroke === 'string' ? normalizeHexColor(obj.originalStroke) : null
    // Usiamo anche i colori correnti oltre agli originali: dopo i remap Pantone
    // successivi il colore "da sostituire" può non coincidere più con quello
    // congelato in `originalFill/originalStroke`.
    const normalizedCurrentFill = typeof obj.fill === 'string' ? normalizeHexColor(obj.fill) : null
    const normalizedCurrentStroke =
      typeof obj.stroke === 'string' ? normalizeHexColor(obj.stroke) : null
    const shouldFill =
      forceAll ||
      isText ||
      isMonochrome ||
      normalizedOriginalFill === normalizedOriginalColorHex ||
      (shouldMatchCurrentColor && normalizedCurrentFill === normalizedOriginalColorHex)
    const shouldStroke =
      forceAll ||
      isText ||
      isMonochrome ||
      (obj.stroke &&
        obj.strokeWidth > 0 &&
        (normalizedOriginalStroke === normalizedOriginalColorHex ||
          (shouldMatchCurrentColor && normalizedCurrentStroke === normalizedOriginalColorHex)))

    if (shouldFill && obj.fill && obj.fill !== 'transparent') obj.set('fill', normalizedNewColorHex)
    if (shouldStroke && obj.stroke && obj.stroke !== 'transparent')
      obj.set('stroke', normalizedNewColorHex)
    if (syncOriginalValue && shouldFill && obj.fill && obj.fill !== 'transparent') {
      obj.originalFill = resolveSyncedOriginalColor(normalizedOriginalFill || normalizedCurrentFill)
    }
    if (syncOriginalValue && shouldStroke && obj.stroke && obj.stroke !== 'transparent') {
      obj.originalStroke = resolveSyncedOriginalColor(
        normalizedOriginalStroke || normalizedCurrentStroke
      )
    }
  }

  const getUpdatedControl = (oc: EditorObjectType, forceUpdate: boolean) => {
    const isMonochrome = !!oc.monochrome

    if (oc.etype === 'text') {
      return {
        ...oc,
        colore: updateEditorColorOriginalValue(oc.colore),
      }
    }

    if (oc.etype === 'image') {
      // `removedBackgroundColor` vive fuori dall'array `colori`, ma deve seguire
      // le stesse regole di riallineamento palette per non desincronizzare sidebar
      // e canvas quando il background viene riaggiunto.
      const removedBackgroundOriginalColor = normalizeHexColor(
        oc.removedBackgroundColor?.originalValue ||
          oc.removedBackgroundColor?.id ||
          oc.removedBackgroundColor?.value
      )
      const removedBackgroundCurrentColor = normalizeHexColor(
        oc.removedBackgroundColor?.value || oc.removedBackgroundColor?.id || ''
      )

      return {
        ...oc,
        colori: (oc.colori || []).map((c) => {
          const slotOriginalColor = normalizeHexColor(c.originalValue || c.id || c.value)
          const slotCurrentColor = normalizeHexColor(c.value || c.id || '')
          // Il match sul valore corrente va usato solo nei riallineamenti automatici.
          // Nei cambi puntuali utente dobbiamo seguire esclusivamente l'identità
          // originale dello slot, altrimenti due slot con lo stesso colore visibile
          // verrebbero aggiornati insieme.
          const isTargetSlot =
            forceUpdate ||
            isMonochrome ||
            slotOriginalColor === normalizedOriginalColorHex ||
            (shouldMatchCurrentColor && slotCurrentColor === normalizedOriginalColorHex)
          return isTargetSlot ? updateEditorColorOriginalValue(c) : c
        }),
        removedBackgroundColor:
          oc.removedBackgroundColor &&
          (forceUpdate ||
            removedBackgroundOriginalColor === normalizedOriginalColorHex ||
            (shouldMatchCurrentColor &&
              removedBackgroundCurrentColor === normalizedOriginalColorHex))
            ? updateEditorColorOriginalValue(oc.removedBackgroundColor)
            : oc.removedBackgroundColor,
      }
    }

    return oc
  }

  const allObjects = canvas.getObjects()
  const objectsToUpdate = isGlobalPantoniColorMode
    ? allObjects.filter((obj: any) => !specialCanvasObjectIds.includes(obj?.customId))
    : allObjects.filter((obj: any) => obj.customId === id)

  if (isGlobalPantoniColorMode) {
    objectsToUpdate.forEach((obj) => applyColorToFabricObject(obj, true, false, false))
  } else {
    const target = allObjects.find((obj: any) => obj.customId === id)
    const targetControl = objectControls.find((oc) => oc.id === id)
    if (target && targetControl) {
      applyColorToFabricObject(
        target,
        false,
        !!targetControl.monochrome,
        targetControl.etype === 'text'
      )
    }
  }

  // Gli elementi nascosti non sono dentro il gruppo visibile: vivono serializzati
  // in `_hiddenColorsData`. Manteniamo quel payload allineato alle stesse regole
  // colore degli oggetti renderizzati, inclusa la modalità globale a colori fissi.
  objectsToUpdate.forEach((target) => {
    if (isGlobalPantoniColorMode) {
      applyHiddenSerializedMonochrome(
        target,
        normalizedNewColorHex,
        syncOriginalValue,
        pantoneReference
      )
      return
    }

    updateHiddenSerializedColors(
      target,
      normalizedOriginalColorHex,
      normalizedNewColorHex,
      syncOriginalValue,
      pantoneReference
    )
  })

  setObjectControls((prevControls) =>
    prevControls.map((oc) => {
      const isTarget = isGlobalPantoniColorMode || oc.id === id
      if (!isTarget) return oc

      const nextControl = getUpdatedControl(oc, isGlobalPantoniColorMode)
      const fabricObj = allObjects.find((obj: any) => obj.customId === oc.id)
      if (fabricObj) (fabricObj as any).editorObjectControl = nextControl
      return nextControl
    })
  )

  onExportOverlayOnly()
  canvas.requestRenderAll()
}

export const handleMonochromaticChange = ({
  canvas,
  id,
  checked,
  objectControls,
  pantoneReference = [],
  setObjectControls,
  onResetToOriginalColor,
  onExportOverlayOnly,
}: {
  canvas: fabric.Canvas | null | undefined
  id: string
  checked: boolean
  objectControls: EditorObjectType[]
  pantoneReference?: EditorColoreApiType[]
  setObjectControls: (updater: (prev: EditorObjectType[]) => EditorObjectType[]) => void
  onResetToOriginalColor: (id: string) => void
  onExportOverlayOnly: () => void
}) => {
  if (!canvas) return

  const control = objectControls.find((oc) => oc.id === id)
  if (!control) return

  const fabricObject = canvas.getObjects().find((obj: any) => obj.customId === id)
  if (!fabricObject) return

  if (checked) {
    const targetColorHex =
      control.etype === 'text' ? control.colore?.value : control.colori?.[0]?.value
    if (!targetColorHex) return

    const applyColor = (obj: any) => {
      if (obj._objects) {
        obj._objects.forEach(applyColor)
      } else {
        if (obj.fill && obj.fill !== 'transparent') {
          obj.set('fill', targetColorHex)
        }
        if (obj.stroke && obj.stroke !== 'transparent' && obj.strokeWidth > 0) {
          obj.set('stroke', targetColorHex)
        }
      }
    }

    applyColor(fabricObject)
    // Gli elementi spenti vengono riaccesi in futuro dal payload hidden: li
    // forziamo allo stesso colore monocromatico per evitare mismatch alla restore.
    applyHiddenSerializedMonochrome(fabricObject, targetColorHex)
  } else {
    onResetToOriginalColor(id)
    // Senza target il helper ripristina originalFill/originalStroke anche per
    // gli elementi che erano nascosti durante il reset monocromatico.
    applyHiddenSerializedMonochrome(fabricObject, null)
  }

  setObjectControls((prevControls) =>
    prevControls.map((oc) => {
      if (oc.id !== id) return oc

      let nextControl: EditorObjectType

      if (oc.etype === 'image') {
        nextControl = {
          ...oc,
          monochrome: checked,
          colori: oc.colori.map((c) => {
            const newValue = normalizeHexColor(
              checked ? oc.colori[0].value : c.originalValue || c.value
            )
            return updateEditorColorValue(c, newValue, pantoneReference)
          }),
        }
      } else {
        const newValue = normalizeHexColor(
          checked ? oc.colore.value : oc.colore.originalValue || oc.colore.value
        )
        nextControl = {
          ...oc,
          monochrome: checked,
          colore: updateEditorColorValue(oc.colore, newValue, pantoneReference),
        }
      }

      ;(fabricObject as any).editorObjectControl = nextControl
      return nextControl
    })
  )

  onExportOverlayOnly()
  canvas.requestRenderAll()
}

export const handleNoBackgroundChange = ({
  canvas,
  id,
  checked,
  objectControls,
  setObjectControls,
  onExportOverlayOnly,
}: {
  canvas: fabric.Canvas | null | undefined
  id: string
  checked: boolean
  objectControls: EditorObjectType[]
  setObjectControls: (updater: (prev: EditorObjectType[]) => EditorObjectType[]) => void
  onExportOverlayOnly: () => void
}) => {
  if (!canvas) return

  const control = objectControls.find((oc) => oc.id === id)
  if (!control) return

  const fabricObject = canvas.getObjects().find((obj: any) => obj.customId === id)
  if (!fabricObject) return

  const backgroundElement = findBackgroundObject(fabricObject)
  if (backgroundElement) {
    backgroundElement.set('visible', !checked)
  }

  const bgSourceColor = backgroundElement?.originalFill ?? backgroundElement?.fill
  const bgNormalizedColor =
    typeof bgSourceColor === 'string' ? normalizeHexColor(bgSourceColor) : null

  setObjectControls((prevControls) =>
    prevControls.map((oc) => {
      if (oc.id !== id) return oc

      if (!bgNormalizedColor || !oc.colori?.length) {
        const nextControl = {
          ...oc,
          noBackground: checked,
        }
        ;(fabricObject as any).editorObjectControl = nextControl
        return nextControl
      }

      if (checked) {
        const index = oc.colori.findIndex(
          (c) => (c.originalValue || c.value).toLowerCase() === bgNormalizedColor
        )
        if (index === -1) {
          const nextControl = {
            ...oc,
            noBackground: checked,
          }
          ;(fabricObject as any).editorObjectControl = nextControl
          return nextControl
        }

        const removedColor = oc.colori[index]
        const nextColors = oc.colori.filter((_, i) => i !== index)

        const nextControl = {
          ...oc,
          noBackground: checked,
          colori: nextColors,
          removedBackgroundColor: removedColor,
          removedBackgroundIndex: index,
        }
        ;(fabricObject as any).editorObjectControl = nextControl
        return nextControl
      }

      const removedColor = oc.removedBackgroundColor
      const removedIndex = oc.removedBackgroundIndex
      if (!removedColor || typeof removedIndex !== 'number') {
        const nextControl = {
          ...oc,
          noBackground: checked,
        }
        ;(fabricObject as any).editorObjectControl = nextControl
        return nextControl
      }

      const alreadyPresent = oc.colori.some((c) => c.id === removedColor.id)
      if (alreadyPresent) {
        const nextControl = {
          ...oc,
          noBackground: checked,
          removedBackgroundColor: undefined,
          removedBackgroundIndex: undefined,
        }
        ;(fabricObject as any).editorObjectControl = nextControl
        return nextControl
      }

      const nextColors = [...oc.colori]
      const safeIndex = Math.max(0, Math.min(removedIndex, nextColors.length))
      nextColors.splice(safeIndex, 0, removedColor)

      const nextControl = {
        ...oc,
        noBackground: checked,
        colori: nextColors,
        removedBackgroundColor: undefined,
        removedBackgroundIndex: undefined,
      }
      ;(fabricObject as any).editorObjectControl = nextControl
      return nextControl
    })
  )

  onExportOverlayOnly()
  canvas.requestRenderAll()
}
